// plugin-integration.js - the shared "Integrate / embed" pack for GDBS plugins.
//
// WHY THIS EXISTS. Every GDBS plugin runs entirely in the browser: deterministic logic plus, for
// the data / reference plugins, an embedded dataset. That makes them naturally bindable by OTHER
// applications - a third-party app can take our data, host our self-contained widget, or (next
// layer) call a plugin as a function - all WITHOUT our servers ever running the computation. That
// is the whole point: the deployment model is the moat, so "let other apps use our tools" must
// mean the customer's own client executes the tool, not that we stand it up as a compute service.
//
// This module is the REUSABLE packaging for that first surface. Any plugin's run(p, host) can call
// host.integrationPack(spec) and get one consistent download + embed + subscribe UI, instead of
// every plugin hand-rolling it. It is deliberately PURE - no DOM, no window, no imports - so it
// behaves identically in the app and under the Node test harness, and so a blob-imported plugin
// reaches it ONLY through the injected host object, never a fresh /js/ import (which a blob module
// cannot resolve against the app module map - see reference_plugin_js_import_blob_gotcha).
//
// WHAT A SUBSCRIPTION BUYS. Not access to a secret endpoint - the math is client-side and the moat
// is the deployment model, not a walled API. A subscription buys staying CURRENT (refreshed,
// re-verified snapshots for data plugins) plus a redistribution / embed license. Stated plainly,
// the same honesty discipline the plugins apply to their own data.

const DEFAULTS = {
  email: 'sales@getvaultsync.com',
  home: 'https://gdbs.getvaultsync.com/',
  accent: '#7c3aed',
};

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// A UTF-8, NON-base64 data URI. Downloads via <a download> with no host JS and no btoa, so it runs
// unchanged under the Node test harness (which has no window/btoa) as well as in the browser.
function dataUri(mime, text) { return 'data:' + mime + ';charset=utf-8,' + encodeURIComponent(text); }

// A small RFC-4180-ish CSV builder so a plugin does not have to reimplement quoting. Columns is an
// array of { key, label?, get?(row) }; get() wins over key when present.
export function toCSV(rows, columns) {
  const cell = (v) => {
    const s = v == null ? '' : Array.isArray(v) ? v.join(' | ') : String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const head = columns.map((c) => c.label || c.key).join(',');
  const body = rows.map((r) => columns.map((c) => cell(c.get ? c.get(r) : r[c.key])).join(',')).join('\n');
  return head + '\n' + body;
}

// Build the "Integrate / embed" result block from a plugin-supplied spec. The plugin owns its DATA
// (it passes a json object, an optional csv string, and an optional self-contained embedHtml
// string); this helper owns the consistent PACKAGING (download links, iframe snippet, subscribe
// CTA) and returns { html, summary, hrefs, subscribeLink } for the plugin to fold into its result.
//
// spec:
//   product        display name of the dataset/tool
//   filename       base name for downloads (-> .json / .csv); no extension
//   id             fallback for filename
//   asOf           snapshot as-of date string (optional; drives the "stays current" copy)
//   json           object -> pretty JSON download (optional)
//   csv            string  -> CSV download (optional)
//   embedHtml      string  -> self-contained widget download + iframe snippet (optional)
//   embedFilename  widget file name (default filename + '.html')
//   iframe         { width, height } for the snippet (defaults 640 x 720)
//   accent         button accent color (default GDBS purple)
//   intro          custom lead sentence (optional)
//   disclaimer     footer disclaimer (optional)
//   subscribe      { email, home, subject } overrides for the CTA (optional)
export function integrationPack(spec) {
  const s = spec || {};
  const sub = Object.assign({}, DEFAULTS, s.subscribe || {});
  const accent = s.accent || DEFAULTS.accent;
  const filename = s.filename || s.id || 'gdbs-plugin';
  const product = s.product || 'GDBS dataset';
  const asOf = s.asOf || '';
  const embedName = s.embedFilename || (filename + '.html');
  const subject = (s.subscribe && s.subscribe.subject) || (product + ' data subscription / embed license');
  const subscribeLink = 'mailto:' + sub.email + '?subject=' + encodeURIComponent(subject);

  const hrefs = {
    json: s.json != null ? dataUri('application/json', JSON.stringify(s.json, null, 2)) : null,
    csv: s.csv != null ? dataUri('text/csv', s.csv) : null,
    widget: s.embedHtml != null ? dataUri('text/html', s.embedHtml) : null,
  };

  const btn = (href, dl, label) => '<a href="' + href + '" download="' + esc(dl)
    + '" style="display:inline-block;margin:0 8px 8px 0;padding:8px 14px;background:' + accent
    + ';color:#fff;border-radius:8px;font-size:12px;font-weight:600;text-decoration:none;">' + esc(label) + '</a>';

  // Numbered sections, but only for the parts this plugin actually offers.
  let n = 0;
  const step = () => (++n) + '. ';

  let html = '<div style="font-size:12px;color:#475569;">'
    + '<p style="margin:0 0 10px;">' + esc(s.intro || ('Integrate ' + product + ' into your own application. '
      + 'Everything below is generated in your browser from the current snapshot, with no key and no server call, '
      + 'so what you download is exactly what you see.')) + '</p>';

  if (hrefs.json || hrefs.csv) {
    html += '<div style="margin-bottom:4px;"><b>' + step() + 'Take the data</b></div>'
      + '<p style="margin:0 0 6px;">Machine-readable and self-describing, ready to pull into your app.</p>'
      + '<div style="margin-bottom:10px;">'
      + (hrefs.json ? btn(hrefs.json, filename + '.json', 'Download JSON') : '')
      + (hrefs.csv ? btn(hrefs.csv, filename + '.csv', 'Download CSV') : '')
      + '</div>';
  }

  if (hrefs.widget) {
    const iw = (s.iframe && s.iframe.width) || 640;
    const ih = (s.iframe && s.iframe.height) || 720;
    const iframe = '<iframe src="' + esc(embedName) + '" title="' + esc(product) + '" width="' + iw + '" height="' + ih
      + '" style="border:1px solid #e2e8f0;border-radius:8px;" loading="lazy"></iframe>';
    html += '<div style="margin-bottom:4px;"><b>' + step() + 'Or embed the ready-made widget</b></div>'
      + '<p style="margin:0 0 6px;">A single self-contained HTML file - host it anywhere and drop it into your page '
      + 'with an iframe. Fully offline, with attribution and source links baked in.</p>'
      + '<div style="margin-bottom:6px;">' + btn(hrefs.widget, embedName, 'Download embeddable widget (HTML)') + '</div>'
      + '<pre style="background:#0f172a;color:#e2e8f0;padding:10px 12px;border-radius:8px;font-size:11px;overflow-x:auto;white-space:pre;margin:0 0 12px;">'
      + esc(iframe) + '</pre>';
  }

  html += '<div style="margin-bottom:4px;"><b>' + step() + 'Keep it current - subscribe</b></div>'
    + '<p style="margin:0 0 8px;">' + esc((asOf ? 'This is a point-in-time snapshot dated ' + asOf + ', and the underlying data changes over time. ' : '')
      + 'A subscription delivers refreshed, re-verified snapshots on a cadence plus a redistribution and embed license, '
      + 'so your integration stays accurate and traceable rather than silently going stale.') + '</p>'
    + '<a href="' + subscribeLink + '" style="display:inline-block;padding:9px 16px;background:#0f172a;color:#fff;border-radius:8px;font-size:12px;font-weight:600;text-decoration:none;">Subscribe / license the feed - ' + esc(sub.email) + '</a>'
    + ' <a href="' + esc(sub.home) + '" target="_blank" rel="noopener" style="margin-left:8px;color:' + accent + ';font-size:12px;">Open the live version</a>';

  if (s.disclaimer) html += '<p style="font-size:11px;color:#94a3b8;margin-top:12px;border-top:1px solid #eef2f7;padding-top:6px;">' + esc(s.disclaimer) + '</p>';
  html += '</div>';

  const formats = [hrefs.json && 'JSON', hrefs.csv && 'CSV', hrefs.widget && 'HTML widget'].filter(Boolean).join(', ');
  const summary = [
    ['Formats', formats || 'none'],
    ['Delivery', 'No key, no network - the data ships inside the file itself'],
    ['Subscription', 'Refreshed snapshots + redistribution / embed license'],
    ['Contact', sub.email],
  ];

  return { html, summary, hrefs, subscribeLink };
}
